/**
 * Sidebar — left navigation shell for the Trident AI platform.
 *
 * Nav items map to the top-level routes (chat, retrieval, audit, agents). Icons are
 * inline SVGs to avoid an extra icon dependency in the scaffold.
 */

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { isAdminRole, useAuth } from '../../contexts/AuthContext';
import { config } from '../../lib/config';

export interface NavItem {
  name: string;
  to: string;
  /** Short SVG path data for a 24x24 viewBox icon. */
  iconPath: string;
  /** Match the route exactly (so a parent link isn't active on child routes). */
  end?: boolean;
  /** Visible only to admin/leadership roles. */
  adminOnly?: boolean;
  /** Demo-era surface: hidden entirely outside mock mode. */
  demoOnly?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    name: 'Chat',
    to: '/chat',
    iconPath: 'M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 17 0Z',
  },
  {
    // Not admin-only: this is the only surface that answers from Trident's own
    // documents, so every user needs it. The API has always allowed any signed
    // in user here and filters results by their own Entra groups.
    name: 'Retrieval',
    to: '/retrieval',
    iconPath: 'm21 21-4.35-4.35M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z',
  },
  {
    // Everyone: each person's own allowance use, as percentages.
    name: 'My allowance',
    to: '/my-allowance',
    iconPath: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm0-10V6m0 6 4 2',
  },
  {
    name: 'Dashboard',
    to: '/dashboard',
    iconPath: 'M3 13h8V3H3v10Zm10 8h8V11h-8v10ZM3 21h8v-6H3v6Zm10-12h8V3h-8v6Z',
    adminOnly: true,
  },
  {
    name: 'Allowances',
    to: '/allowances',
    iconPath: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    adminOnly: true,
  },
  {
    name: 'Audit',
    to: '/audit',
    iconPath: 'M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
    adminOnly: true,
  },
  {
    name: 'Evals',
    to: '/evals',
    demoOnly: true,
    iconPath: 'M9 17V9m4 8V5m4 12v-6M4 21h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1Z',
  },
  {
    name: 'Agents',
    to: '/agents',
    end: true,
    demoOnly: true,
    iconPath: 'M6 3v12M18 9v12M6 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm12-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 0a9 9 0 0 1-9 9',
  },
  {
    name: 'Tools',
    to: '/tools',
    demoOnly: true,
    iconPath: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-2.5 2.5-2.5Z',
  },
  {
    name: 'Connectors',
    to: '/connectors',
    adminOnly: true,
    iconPath: 'M9 7H6a3 3 0 0 0 0 6h3m6 0h3a3 3 0 0 0 0-6h-3M8 10h8',
  },
  {
    name: 'Run History',
    to: '/agents/runs',
    demoOnly: true,
    iconPath: 'M12 8v4l3 3M3.05 11a9 9 0 1 1 .5 4M3 8v4h4',
  },
];

function NavIcon({ path }: { path: string }): ReactNode {
  return (
    <svg
      className="w-5 h-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

export function Sidebar(): ReactNode {
  const { logout, user } = useAuth();

  // Admin surfaces gate by role everywhere (API enforces the same server-side);
  // demo-era surfaces exist only in mock builds.
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.demoOnly && !config.mockMode) return false;
    if (item.adminOnly && !isAdminRole(user?.role)) return false;
    return true;
  });

  const linkClass = ({ isActive }: { isActive: boolean }): string =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
      isActive
        ? 'bg-navy-700 text-white'
        : 'text-navy-100/80 hover:bg-navy-800 hover:text-white'
    }`;

  return (
    <aside className="w-60 shrink-0 bg-navy-900 text-white flex flex-col print:hidden">
      {/* Brand */}
      <div className="h-16 flex items-center px-4 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="/apple-touch-icon.png"
            alt=""
            className="w-8 h-8 shrink-0"
          />
          <span className="font-semibold leading-tight truncate">
            Trident AI
            <span className="block text-[11px] text-steel-300 font-normal">
              Trident Systems
            </span>
          </span>
        </div>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.end} className={linkClass}>
                <NavIcon path={item.iconPath} />
                <span className="truncate">{item.name}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-white/10 py-3 px-2">
        <button
          type="button"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-navy-100/80 hover:bg-navy-800 hover:text-white transition-colors"
        >
          <NavIcon path="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
